import { renderProductPopup, cardActionSelector, swapCardAction } from './product-popup.js';
import {
  salePriceLabelHTML,
  comparePriceHTML,
  swatchGroupAttrs,
  swatchButtonHTML,
  staticSwatchHTML,
  labelSwatch,
  setActiveSwatch,
} from './card-a11y.js';
/**
 * Section: Collection Grid
 * ------------------------------------------------------------------------------
 * Hybrid approach:
 * - Initial render: Liquid with LQIP (fast, no JS wait)
 * - JS enhances: Fetches related products for swatches
 * - Filtering/sorting: Uses GraphQL and replaces grid
 */

class CollectionGrid extends HTMLElement {
  constructor() {
    super();
    this.accessToken = '8bcb2ea2ba9acd5b735e7abe4cbca7d0';
    this.graphUrl =
      'https://liquiddeath.myshopify.com/api/2025-07/graphql.json';
    this.numProducts = window.innerWidth > 1024 ? 32 : 16;
    this.cursor = null;
    this.hasNextPage = false;
    this.collection = this.dataset.handle;
    this.grid = this.querySelector('#products-grid');
    this.page = 1;
    this.market = this.getAttribute('data-market');

    this.filters = null;
    this.sortKey = null;
    this.countryClubCustomer = this.getAttribute('countryClubUser');
    this.context = this.getAttribute('data-market').toUpperCase();
    this.currency = this.getAttribute('data-currency');
    this.scrolled = false;
    this.isLiquidRendered = this.grid?.dataset.initialRender === 'true';
  }

  connectedCallback() {
    if (this.isLiquidRendered) {
      // Products already rendered by Liquid - just enhance with swatches
      this.initFromLiquid();
    } else {
      // Fallback to full JS render
      this.fetchProducts();
    }

    this.pagination();
    this.sorting();
    this.filtering();
    this.toggleFilters();
    this.mobileGridView();
    this.changeVariant();
  }

  /**
   * Initialize from Liquid-rendered content
   * Fetches related products for swatches without replacing existing products
   */
  initFromLiquid() {
    console.log('[LQIP] initFromLiquid started');

    // Read pagination state
    const paginationData = document.getElementById('pagination-data');
    if (paginationData) {
      try {
        const data = JSON.parse(paginationData.textContent);
        this.hasNextPage = data.hasNextPage;
        this.page = data.currentPage;
      } catch (e) {
        console.error('Failed to parse pagination data:', e);
      }
    }

    // Collect all related swatch IDs from Liquid-rendered products
    const relatedIds = new Set();
    const productCards = this.grid.querySelectorAll('.product-grid-item[data-related-swatches]');
    console.log('[LQIP] Found product cards with related-swatches:', productCards.length);

    productCards.forEach((card) => {
      const relatedData = card.dataset.relatedSwatches;
      console.log('[LQIP] Card related data:', relatedData);
      if (relatedData && relatedData !== 'null' && relatedData !== '[]') {
        try {
          const ids = JSON.parse(relatedData);
          console.log('[LQIP] Parsed IDs:', ids);
          if (Array.isArray(ids)) {
            ids.forEach(id => relatedIds.add(id));
          }
        } catch (e) {
          console.error('[LQIP] Parse error:', e);
        }
      }
    });

    console.log('[LQIP] Total related IDs to fetch:', relatedIds.size, Array.from(relatedIds));

    // Fetch related products and add them to the grid (hidden)
    if (relatedIds.size > 0) {
      this.fetchRelatedProductsForSwatches(Array.from(relatedIds));
    } else {
      console.log('[LQIP] No related products to fetch');
    }

    // Process existing swatches
    this.processSwatches();
  }

  /**
   * Fetch related products for swatch functionality
   * These are rendered hidden and used for swatch color switching
   */
  fetchRelatedProductsForSwatches(ids) {
    console.log('[LQIP] fetchRelatedProductsForSwatches called with:', ids);
    const self = this;
    const query = `query @inContext(country: ${this.context}) {
      nodes(ids: ${JSON.stringify(ids)}) {
        ... on Product {
          id
          handle
          title
          featuredImage {
            transformedSrc(maxWidth: 400)
          }
          buttonDisabled: metafield(namespace: "custom", key: "product_button_disabled") { value }

          popupEnabled: metafield(namespace: "custom", key: "klaviyo_popup_enabled") { value }
              popupFormId: metafield(namespace: "custom", key: "klaviyo_form_id") { value }
              popupButtonText: metafield(namespace: "custom", key: "klaviyo_button_text") { value }
              metafields(identifiers: [{namespace: "custom", key: "swatch"}, {namespace: "pdp", key: "amazon_link"}, {namespace: "pdp", key: "amazon_button_text"}, {namespace: "pdp", key: "announcement_tag"}, {namespace: "custom", key: "related_swatches"}]) {
            value
          }
          tags
          variants(first: 100) {
            edges {
              node {
                id
                price { amount }
                title
                availableForSale
                quantityAvailable
              }
            }
          }
          options {
            name
            values
          }
        }
      }
    }`;

    fetch(this.graphUrl, {
      method: 'POST',
      headers: {
        'X-Shopify-Storefront-Access-Token': this.accessToken,
        'Content-Type': 'application/graphql',
      },
      body: query,
    })
      .then((res) => res.json())
      .then((res) => {
        console.log('[LQIP] GraphQL response:', res);
        const products = res.data?.nodes || [];
        console.log('[LQIP] Products fetched:', products.length);
        let html = '';

        products.forEach((product) => {
          if (product) {
            // Check if this product is already in the grid
            const decodedId = this.decodeId(product.id);
            const escapedId = CSS.escape(String(decodedId));
            const existingProduct = this.grid.querySelector(`.product-grid-item.${escapedId}`);
            console.log('[LQIP] Product', decodedId, 'already exists:', !!existingProduct);
            if (!existingProduct) {
              html += self.renderTemplate({ node: product }, 'parent');
            }
          }
        });

        if (html) {
          console.log('[LQIP] Adding related products to DOM');
          this.grid.insertAdjacentHTML('beforeend', html);
        }

        // Now rebuild swatches for all products that have related swatches
        console.log('[LQIP] Rebuilding swatches...');
        this.rebuildSwatches();
        this.processSwatches();
      })
      .catch((error) => console.error('[LQIP] Failed to fetch related products:', error));
  }

  /**
   * Rebuild swatches for Liquid-rendered products now that related products are loaded
   */
  rebuildSwatches() {
    const productCards = this.grid.querySelectorAll('.product-grid-item[data-related-swatches]');
    console.log('[LQIP] rebuildSwatches: found', productCards.length, 'cards');

    productCards.forEach((card) => {
      const relatedData = card.dataset.relatedSwatches;
      const productId = card.dataset.productId;
      const swatchContainer = card.querySelector('.product-swatches');

      if (!swatchContainer) {
        console.log('[LQIP] rebuildSwatches: no swatch container for product', productId);
        return;
      }
      if (!relatedData || relatedData === 'null' || relatedData === '[]') {
        console.log('[LQIP] rebuildSwatches: no related data for product', productId);
        return;
      }

      try {
        const relatedIds = JSON.parse(relatedData);
        if (!Array.isArray(relatedIds) || relatedIds.length === 0) {
          console.log('[LQIP] rebuildSwatches: empty related IDs for product', productId);
          return;
        }

        // Build sorted list of all swatch IDs (current product + related)
        const allIds = [parseInt(productId), ...relatedIds.map(id => this.decodeId(id))].sort((a, b) => a - b);
        console.log('[LQIP] rebuildSwatches: product', productId, 'all IDs:', allIds);

        // Rebuild swatch HTML
        let swatchHtml = '';
        allIds.forEach((id) => {
          const isActive = id === parseInt(productId);
          swatchHtml += swatchButtonHTML({ id, active: isActive });
        });

        swatchContainer.innerHTML = swatchHtml;
        console.log('[LQIP] rebuildSwatches: rebuilt swatches for product', productId);
      } catch (e) {
        console.error('[LQIP] rebuildSwatches: parse error for product', productId, e);
      }
    });
  }

  /**
   * Process swatches to add color classes based on product data-color attributes
   */
  processSwatches() {
    const swatches = document.querySelectorAll('.swatch');
    swatches.forEach((swatch) => {
      const swatchId = swatch.dataset.swatch;
      if (swatchId) {
        const escapedSwatchId = CSS.escape(swatchId);
        const productElement = document.querySelector(`.product-grid-item.${escapedSwatchId}`);
        if (productElement) {
          const color = productElement.dataset.color;
          if (color) {
            const colorClass = color
              .toLowerCase()
              .replaceAll(' ', '-')
              .replaceAll('/', '-');
            swatch.classList.add(colorClass);
            labelSwatch(swatch, color);
          }
        }
      }
    });
  }

  toggleFilters() {
    if (window.location.href.indexOf('openFilter') > -1) {
      const filtersWrap = document.querySelector('.filters-wrap');
      if (filtersWrap) {
        filtersWrap.classList.remove('hidden');
        setTimeout(() => {
          const filtersWrapInner = document.querySelector('.filters-wrap-inner');
          if (filtersWrapInner) {
            filtersWrapInner.classList.add('active');
          }
        });
        const filterSortLabel = document.querySelector('.filter-sort label');
        if (filterSortLabel) {
          filterSortLabel.focus();
        }
      }
    }

    const openFilters = document.querySelector('.open-filters');
    if (openFilters) {
      openFilters.addEventListener('click', function () {
        const filtersWrap = document.querySelector('.filters-wrap');
        if (filtersWrap) {
          filtersWrap.classList.remove('hidden');
          setTimeout(() => {
            const filtersWrapInner = document.querySelector('.filters-wrap-inner');
            if (filtersWrapInner) {
              filtersWrapInner.classList.add('active');
            }
          });
          const filterSortLabel = document.querySelector('.filter-sort label');
          if (filterSortLabel) {
            filterSortLabel.focus();
          }
        }
      });
    }

    const closeFiltersElements = document.querySelectorAll('.close-filters');
    closeFiltersElements.forEach((closeFilter) => {
      closeFilter.addEventListener('click', function () {
        const filtersWrapInner = document.querySelector('.filters-wrap-inner');
        if (filtersWrapInner) {
          filtersWrapInner.classList.remove('active');
          setTimeout(() => {
            const filtersWrap = document.querySelector('.filters-wrap');
            if (filtersWrap) {
              filtersWrap.classList.add('hidden');
            }
          }, 500);
        }
      });
    });
  }

  productQuery() {
    let arg = this.cursor
      ? `first: ${this.numProducts} after: "${this.cursor}"`
      : `first: ${this.numProducts}`;
    return `query @inContext(country: ${this.context}){
                collection(handle: "${this.collection}") {
                    products(${arg}, filters: ${this.filters} , sortKey:${this.sortKey}) {
                        edges {
                            cursor
                            node{
                                handle
                                id
                                title
                                featuredImage {
                                  transformedSrc(maxWidth: 400)
                                }
                                buttonDisabled: metafield(namespace: "custom", key: "product_button_disabled") { value }

                                popupEnabled: metafield(namespace: "custom", key: "klaviyo_popup_enabled") { value }
              popupFormId: metafield(namespace: "custom", key: "klaviyo_form_id") { value }
              popupButtonText: metafield(namespace: "custom", key: "klaviyo_button_text") { value }
              metafields(identifiers: [{namespace: "custom", key: "swatch"}, {namespace: "pdp", key: "amazon_link"}, {namespace: "pdp", key: "amazon_button_text"}, {namespace: "pdp", key: "announcement_tag"}, {namespace: "custom", key: "related_swatches"}]) {
                                  value
                                }
                                tags
                                variants(first: 100) {
                                    edges {
                                        node {
                                            id
                                            price{
                                                amount
                                            }
                                            compareAtPrice {
                                              amount
                                            }
                                            title
                                            availableForSale
                                            quantityAvailable
                                            image{
                                                url
                                            }
                                        }
                                    }
                                }
                                options {
                                    name
                                    values
                                }
                            }
                        }
                        pageInfo {
                            hasNextPage
                        }
                    }
                }
            }`;
  }

  changeVariant() {
    const self = this;

    document.addEventListener('click', function (e) {
      // Handle swatch clicks
      if (e.target.classList.contains('swatch')) {
        self.handleSwatchClick(e.target);
      }

      // Update filter counts on any click
      const selectedColorAmount =
        '(' +
        document.querySelectorAll(
          '.filters-wrap input[type="checkbox"][data-type="Color"]:checked'
        ).length +
        ')';
      const selectedSizeAmount =
        '(' +
        document.querySelectorAll(
          '.filters-wrap input[type="checkbox"][data-type="Size"]:checked'
        ).length +
        ')';
      const selectedFilterAmount =
        '(' +
        document.querySelectorAll(
          '.filters-wrap input[type="checkbox"]:checked'
        ).length +
        ')';

      const sortSize = document.getElementById('sort-size');
      const resetFilters = document.querySelector('.reset-filters');
      const sortColor = document.getElementById('sort-color');

      if (sortSize) sortSize.textContent = 'Shop by Size ' + selectedSizeAmount;
      if (resetFilters)
        resetFilters.textContent = 'RESET ' + selectedFilterAmount;
      if (sortColor)
        sortColor.textContent = 'Shop by Color ' + selectedColorAmount;
    });
  }

  handleSwatchClick(swatch) {
    console.log('[LQIP] handleSwatchClick called');
    const swatchProductId = swatch.dataset.swatch;
    const originalParent = swatch.closest('.product-grid-item');

    console.log('[LQIP] Swatch data:', { swatchProductId, isActive: swatch.classList.contains('active') });

    if (!originalParent) {
      console.log('[LQIP] No parent found, returning');
      return;
    }

    // Only process if clicking a non-active swatch
    if (swatchProductId && !swatch.classList.contains('active')) {
      const escapedId = CSS.escape(String(swatchProductId));

      // Check if this card is already displaying the requested product
      const currentlyDisplayed = originalParent.dataset.displayedProductId || originalParent.dataset.productId;
      if (currentlyDisplayed === swatchProductId) {
        console.log('[LQIP] Card already displays this product');
        // Just update active state
        setActiveSwatch(swatch, originalParent);
        return;
      }

      // Find the related product card to get its data (look for original, not updated cards)
      // Exclude the current card - we need to get data from a DIFFERENT card
      const selector = `.product-grid-item.${escapedId}`;
      console.log('[LQIP] Looking for cards with selector:', selector);
      const allMatching = document.querySelectorAll(selector);
      console.log('[LQIP] Found', allMatching.length, 'matching cards');
      let relatedProduct = null;
      for (const card of allMatching) {
        console.log('[LQIP] Card:', card.dataset.productId, 'is originalParent:', card === originalParent);
        if (card !== originalParent) {
          relatedProduct = card;
          break;
        }
      }
      console.log('[LQIP] Found related product (different card):', !!relatedProduct);

      if (relatedProduct) {
        // Store original data before first update (for switching back)
        if (!originalParent.dataset.originalDataStored) {
          const origImage = originalParent.querySelector('.lqip-container img, img');
          const origLink = originalParent.querySelector('a');
          const origTitle = originalParent.querySelector('.product-grid-item__title');
          originalParent.dataset.originalAction = originalParent.querySelector(cardActionSelector)?.outerHTML || '';
          const origCta = originalParent.querySelector('.product-cta');

          originalParent.dataset.originalDataStored = 'true';
          originalParent.dataset.originalImageSrc = origImage?.src || '';
          originalParent.dataset.originalImageAlt = origImage?.alt || '';
          originalParent.dataset.originalLink = origLink?.href || '';
          originalParent.dataset.originalTitle = origTitle?.textContent || '';
          originalParent.dataset.originalColor = originalParent.dataset.color || '';
          if (origCta) {
            originalParent.dataset.originalCtaHandle = origCta.dataset.handle || '';
            originalParent.dataset.originalCtaColor = origCta.dataset.color || '';
            originalParent.dataset.originalCtaId = origCta.dataset.id || '';
          }
          console.log('[LQIP] Stored original data for card', originalParent.dataset.productId);
        }

        // Update current card IN-PLACE with related product's data
        // Get data from related product
        const relatedImage = relatedProduct.querySelector('.lqip-container img, img');
        const relatedLink = relatedProduct.querySelector('a');
        const relatedTitle = relatedProduct.querySelector('.product-grid-item__title');
        const relatedColor = relatedProduct.dataset.color;

        // Update image (direct swap, no LQIP reset to avoid flash)
        const currentImage = originalParent.querySelector('.lqip-container img, img');
        if (currentImage && relatedImage) {
          currentImage.src = relatedImage.src;
          currentImage.alt = relatedImage.alt;
        }

        // Update link URLs
        if (relatedLink) {
          const newUrl = relatedLink.href;
          originalParent.querySelectorAll('a').forEach(link => {
            link.href = newUrl;
          });
        }

        // Update title
        const currentTitle = originalParent.querySelector('.product-grid-item__title');
        if (currentTitle && relatedTitle) {
          currentTitle.textContent = relatedTitle.textContent;
        }

        // Update CTA data attributes
        swapCardAction(originalParent, relatedProduct);

        // Store currently displayed product ID (but DON'T change the class to avoid duplicates)
        originalParent.dataset.displayedProductId = swatchProductId;
        originalParent.dataset.color = relatedColor;

        // Update active state on swatches
        setActiveSwatch(swatch, originalParent);

        console.log('[LQIP] Updated product card in-place');
      } else {
        // Can't find another card - check if we have stored original data to restore
        const originalProductId = originalParent.dataset.productId;
        if (swatchProductId === originalProductId && originalParent.dataset.originalDataStored === 'true') {
          console.log('[LQIP] Restoring original data for card', originalProductId);

          // Restore image (direct swap, no LQIP reset to avoid flash)
          const currentImage = originalParent.querySelector('.lqip-container img, img');
          if (currentImage && originalParent.dataset.originalImageSrc) {
            currentImage.src = originalParent.dataset.originalImageSrc;
            currentImage.alt = originalParent.dataset.originalImageAlt;
          }

          // Restore links
          if (originalParent.dataset.originalLink) {
            originalParent.querySelectorAll('a').forEach(link => {
              link.href = originalParent.dataset.originalLink;
            });
          }

          // Restore title
          const currentTitle = originalParent.querySelector('.product-grid-item__title');
          if (currentTitle && originalParent.dataset.originalTitle) {
            currentTitle.textContent = originalParent.dataset.originalTitle;
          }

          // Restore CTA
          const currentCta = originalParent.querySelector(cardActionSelector);
          if (currentCta && originalParent.dataset.originalAction) {
            currentCta.outerHTML = originalParent.dataset.originalAction;
          }

          // Update displayed product and color
          originalParent.dataset.displayedProductId = originalProductId;
          originalParent.dataset.color = originalParent.dataset.originalColor;
        } else {
          console.log('[LQIP] No other card found and no stored data for', swatchProductId);
        }

        // Update active state
        setActiveSwatch(swatch, originalParent);
      }
    }
  }

  decodeId(id) {
    if (typeof id === 'number') return id;
    const shopifyId = String(id).split('/').pop();
    return parseInt(shopifyId);
  }

  renderTemplate(product, parent) {
    let self = this;
    let item = product.node;
    if (parent) {
      item = product;
    }

    let image = item.featuredImage.transformedSrc;

    // Generate LQIP placeholder URL (20px wide)
    let placeholderUrl = image.replace(/width=\d+/, 'width=20');
    if (placeholderUrl === image) {
      placeholderUrl = image + (image.includes('?') ? '&' : '?') + 'width=20';
    }

    let market = this.market;
    if (market === 'us') {
      market = '/';
    } else {
      market = '/en-' + market + '/';
    }

    const variantPrice = Number(item.variants.edges[0].node.price.amount);
    let price = variantPrice.toFixed(2);
    let comparePrice = '';

    // Check if this product is excluded from promo
    const isExcluded =
      window.promoExcludedProducts?.includes(item.handle) || false;

    // Apply promotional pricing if enabled and not excluded
    if (
      window.showPromoOriginalPrice &&
      window.promoDiscountPercentage > 0 &&
      !isExcluded
    ) {
      const discountMultiplier = (100 - window.promoDiscountPercentage) / 100;
      price = (variantPrice * discountMultiplier).toFixed(2);

      if (item.variants.edges[0].node.compareAtPrice) {
        comparePrice = comparePriceHTML(
          `$${Number(item.variants.edges[0].node.compareAtPrice.amount).toFixed(2)}`
        );
      } else {
        comparePrice = comparePriceHTML(
          `$${variantPrice.toFixed(2)}`,
          'data-promo-calculated="true"'
        );
      }
    } else if (item.variants.edges[0].node.compareAtPrice) {
      comparePrice = comparePriceHTML(
        `$${Number(item.variants.edges[0].node.compareAtPrice.amount).toFixed(2)}`
      );
    }

    let selectedColor =
      item.options.filter((option) => option.name.toLowerCase() == 'color')[0]
        ?.values[0] || null;

    let available = item.variants.edges.filter(
      (variant) => variant.node.availableForSale
    ).length;

    let countryClubProduct = item.tags.includes('country-club-exclusive');

    let swatches = [];
    if (item.metafields[4]) {
      swatches.push(self.decodeId(item.id));
      JSON.parse(item.metafields[4].value).forEach(function (product) {
        swatches.push(self.decodeId(product));
      });
    }
    swatches = swatches.sort();

    // Check if there are any checked size inputs
    const sizeCheckedInputs = document.querySelectorAll(
      '.filter-size input[data-size-value]:checked'
    );
    let unavailableClass = '';
    let availableSize = '';
    let extractedId = '';

    if (sizeCheckedInputs.length) {
      let allSizesUnavailable = true;

      sizeCheckedInputs.forEach(function (input) {
        const sizeValue = input.dataset.sizeValue;
        let sizeAvailable = false;

        item.variants.edges.forEach((variant) => {
          let variantParts = variant.node.title.split(' / ');
          let variantSize = variantParts[1];

          if (variantSize === sizeValue && variant.node.availableForSale) {
            sizeAvailable = true;
            extractedId = variant.node.id.split('/').pop();
          }
        });

        if (sizeAvailable) {
          allSizesUnavailable = false;
        }
      });

      if (allSizesUnavailable) {
        unavailableClass = 'unavailable-size';
      } else if (sizeCheckedInputs.length == 1) {
        availableSize = 'available-size';
      }
    }

    // Build HTML with LQIP container
    let html = `
      <div class="product-grid-item ${unavailableClass} ${self.decodeId(item.id)} ${parent ? 'parent-product' : ''}" data-color="${item.metafields[0]?.value}" data-product-id="${self.decodeId(item.id)}">
        <div class="relative">
          <a href="${market}products/${item.handle}" class="block">
            <div class="lqip-container" style="aspect-ratio: 1; background-image: url('${placeholderUrl}'); background-size: cover; background-position: center;">
              <img class="w-full h-full object-cover" src="${image}" alt="${item.title}" loading="lazy" onload="this.parentElement.classList.add('lqip-loaded'); this.parentElement.style.backgroundImage = 'none';" />
            </div>
          </a>`;

    if (item.metafields[3] != null) {
      html += `<span class="default-badge absolute"><span class='block relative -mb-[2px]'>${item.metafields[3].value}</span></span>`;
    }

    html += `
      <span class="absolute sold-out-badge ${
        (available || item.metafields[1] != null) && '!hidden'
      }"><span class='block relative -mb-[2px]'>Sold out</span></span>
    </div>
    <a href="/products/${item.handle}" class="block">
      <div class="text-dark product-grid-item__title">${item.title}</div>
    </a>
    <div class="text-dark product-grid-item__price">${comparePrice ? salePriceLabelHTML() : ''}${this.currency}${price}${comparePrice ? ' ' + comparePrice : ''}</div>`;

    if (swatches.length || selectedColor) {
      html += `<div class="product-swatches ml-6 md:ml-0 my-2" ${swatchGroupAttrs()}>`;
      if (swatches.length) {
        swatches.forEach(function (swatch) {
          html += swatchButtonHTML({ id: swatch, active: swatch == self.decodeId(item.id) });
        });
      } else if (selectedColor) {
        let color = selectedColor.toLowerCase().replaceAll(' ', '-').replaceAll('/', '-');
        html += staticSwatchHTML(color, selectedColor);
      }
      html += `</div>`;
    }

    if (item.buttonDisabled?.value === 'true') {
      html += renderProductPopup(item) + '</div>';
    } else if (countryClubProduct && this.countryClubCustomer == 'false') {
      html += `<a href="/pages/sell-your-soul" class="text-dark uppercase cursor-pointer font-bold ml-6 md:ml-0">Country Club Exclusive</a></div>`;
    } else if (renderProductPopup(item)) {
      html += renderProductPopup(item) + '</div>';
    } else if (item.metafields[1] != null) {
      const amazonUrl = item.metafields[1].value.replace(/[“”„‟″‶]/g, '').trim();
      const amazonText = (item.metafields[2]?.value || 'Buy on Amazon').replace(/[“”„‟″‶]/g, '').trim();
      html += `<a target="_blank" aria-label="Opens in new window" href="${amazonUrl}" class="text-dark uppercase cursor-pointer font-bold ml-6 md:ml-0" data-handle="${item.handle}">${amazonText}</a></div>`;
    } else if (!available) {
      html += `
    <klaviyo-bis product-id='${this.decodeId(item.id)}' variant-id='${this.decodeId(item.variants.edges[0].node.id)}'>
    <a class='klaviyo-bis-trigger ml-6 md:ml-0 block text-dark uppercase cursor-pointer font-bold bg-white' href='#'>Notify Me When Available</a>
    <div class='klaviyo-bis-modal-overlay fixed z-50 inset-0 overflow-y-auto hidden bg-black/50 flex items-center justify-center' aria-labelledby='modal-title' role='dialog' aria-modal='true'>
      <div id='klaviyo-bis-modal' class='relative'>
        <div class='bg-white relative border border-gray-300 shadow-lg' style='padding: 32px 18px 40px 18px; max-width: 460px;' id='container'>
          <button type='button' class='close klaviyo-bis-close' data-dismiss='modal'>×</button>
          <h3 class='modal-title'>${item.title}</h3>
          <p>Register to receive a notification when this item comes back in stock.</p>
          <form action='#' class='form-horizontal clearfix'>
            <div class='form-group' style='margin-bottom: 15px;'>
              <div id='variant_select' class='col-sm-12' style='display: none;'>
                <input type='hidden' id='product' value='${this.decodeId(item.variants.edges[0].node.id)}'>
                <select id='variants' class='selectpicker form-control input-lg'>`;
      item.variants.edges.forEach((variantEdge) => {
        let variant = variantEdge.node;
        html += `<option value='${this.decodeId(variant.id)}'>${variant.title}</option>`;
      });
      html += `</select>
              </div>
            </div>
            <div class='form-group' style='margin-bottom: 15px;'>
              <div class='col-sm-12'>
                <input required type='email' id='email' placeholder='Email' class='form-control input-lg' value=''>
              </div>
              <div id='newsletter_subscription' style='display: none;'>
                <p><input type='checkbox' id='subscribe_for_newsletter' checked=''><label for='subscribe_for_newsletter' style='margin-left:10px;'>Add me to your email list.</label></p>
              </div>
            </div>
            <div class='control-group clearfix submit-container'>
              <button type='submit' class='btn btn-notify btn-success btn-lg col-xs-12 hover:!bg-gold'>Notify me when available</button>
            </div>
            <p class='text-sm'>By clicking "Notify Me" you agree to be brainwashed by Liquid Death marketing through rare (but hilarious) emails and agreeing to our <a class='underline text-[#8a6d35]' href='/policies/terms-of-service'>Terms & Conditions</a> / <a class='underline text-[#8a6d35]' href='/policies/privacy-policy'>Privacy Policy</a></p>
            <div id='error_message' class='error_message alert alert-danger hidden'></div>
            <div id='completed_message' class='completed_message alert alert-success hidden'>You're in! We'll let you know when it's back. <a href='#' class='klaviyo-bis-close'>Close</a></div>
          </form>
          <p class='small-print'></p>
        </div>
      </div>
    </div>
    </klaviyo-bis>
    </div>`;
    } else {
      html += `<span class="product-cta ${availableSize} text-dark uppercase cursor-pointer font-bold" data-handle="${item.handle}" data-color="${selectedColor}" data-id="${extractedId || this.decodeId(item.variants.edges[0].node.id)}">`;
      html += item.metafields[2]?.value != null ? item.metafields[2].value : 'Add to Cart';
      html += `</span></div>`;
    }

    return html;
  }

  renderGrid(products, append) {
    const self = this;
    let relatedProductIds = [];
    this.cursor = `${products[products.length - 1]?.cursor}`;
    let productCards = '';

    // Get existing product IDs to avoid duplicates when appending
    const existingIds = append ? this.getExistingProductIds() : new Set();

    products.forEach((product) => {
      // Skip products that already exist in the DOM
      const productId = String(this.decodeId(product.node.id));
      if (existingIds.has(productId)) {
        return;
      }
      productCards += self.renderTemplate(product);

      if (product.node.metafields[4]) {
        let arr = JSON.parse(product.node.metafields[4].value);
        arr.forEach(function (id) {
          relatedProductIds.push(id);
        });
      }
    });

    if (relatedProductIds.length) {
      this.getRelatedProducts
        .call(this, relatedProductIds)
        .then((html) => {
          productCards += html;

          if (append) {
            this.querySelector('products-grid').innerHTML += productCards;
          } else {
            // Clear initial render flag when replacing content
            this.grid.removeAttribute('data-initial-render');
            this.querySelector('products-grid').innerHTML = productCards;
          }
          this.processSwatches();
        })
        .catch((error) => console.error(error));
    } else {
      if (append) {
        this.querySelector('products-grid').innerHTML += productCards;
      } else {
        this.grid.removeAttribute('data-initial-render');
        this.querySelector('products-grid').innerHTML = productCards;
      }
      this.processSwatches();
    }

    const productNum = window.innerWidth > 1024 ? self.page * 32 : self.page * 16;
    const productItems = document.querySelectorAll('.product-grid-item');
    for (let i = 0; i < productNum && i < productItems.length; i++) {
      productItems[i].classList.remove('!hidden');
    }
  }

  getRelatedProducts(ids) {
    const self = this;
    let query = `query @inContext(country: ${this.context}) {
      nodes(ids: ${JSON.stringify(ids)}) {
        ... on Product {
          id
          handle
          title
          featuredImage {
            transformedSrc(maxWidth: 400)
          }
          buttonDisabled: metafield(namespace: "custom", key: "product_button_disabled") { value }

          popupEnabled: metafield(namespace: "custom", key: "klaviyo_popup_enabled") { value }
              popupFormId: metafield(namespace: "custom", key: "klaviyo_form_id") { value }
              popupButtonText: metafield(namespace: "custom", key: "klaviyo_button_text") { value }
              metafields(identifiers: [{namespace: "custom", key: "swatch"}, {namespace: "pdp", key: "amazon_link"}, {namespace: "pdp", key: "amazon_button_text"}, {namespace: "pdp", key: "announcement_tag"}, {namespace: "custom", key: "related_swatches"}]) {
            value
          }
          tags
          variants(first: 100) {
            edges {
              node {
                id
                price { amount }
                title
                availableForSale
                quantityAvailable
              }
            }
          }
          options {
            name
            values
          }
        }
      }
    }`;

    return new Promise((resolve, reject) => {
      fetch(this.graphUrl, {
        method: 'POST',
        headers: {
          'X-Shopify-Storefront-Access-Token': this.accessToken,
          'Content-Type': 'application/graphql',
        },
        body: query,
      })
        .then((res) => res.json())
        .then((res) => {
          let response = res.data.nodes;
          let html = '';
          response.forEach((product) => {
            if (product) {
              html += self.renderTemplate(product, 'parent');
            }
          });
          resolve(html);
        })
        .catch((error) => reject(error));
    });
  }

  fetchProducts(append) {
    fetch(this.graphUrl, {
      method: 'POST',
      headers: {
        'X-Shopify-Storefront-Access-Token': this.accessToken,
        'Content-Type': 'application/graphql',
      },
      body: this.productQuery(),
    })
      .then((res) => res.json())
      .then((res) => {
        let response = res.data.collection.products;
        if (response.edges == 0) {
          const productsGrid = document.getElementById('products-grid');
          if (productsGrid) {
            productsGrid.innerHTML = `
              <div class="col-span-full flex flex-col items-center justify-center py-20 px-6 text-center">
                <svg class="w-16 h-16 text-light mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                </svg>
                <h3 class="text-xl font-bold text-dark mb-2 uppercase">No products found</h3>
                <p class="text-medium text-base max-w-md">Sorry, we couldn't find any products that match your search criteria. Try adjusting your filters or browse our other collections.</p>
                <a href="/collections/merch-store" class="mt-6 button-white inline-flex items-center justify-center px-6 py-3 font-bold uppercase text-base">Shop All Merch</a>
              </div>
            `;
          }
        } else {
          this.renderGrid(response.edges, append);
        }

        this.hasNextPage = response.pageInfo.hasNextPage;

        const loadMore = document.querySelector('.load-more');
        if (this.hasNextPage) {
          if (loadMore) loadMore.classList.remove('hidden');
        } else {
          if (loadMore) loadMore.classList.add('hidden');
          const productItems = document.querySelectorAll('.product-grid-item');
          productItems.forEach((item) => item.classList.remove('!hidden'));
        }
      });
  }

  pagination() {
    const self = this;
    const section = document.querySelector('products-grid');

    window.addEventListener('scroll', function () {
      const scroll = window.pageYOffset;
      const sectionHeight = section.offsetHeight;
      const sectionOffsetTop = section.offsetTop;
      const targetHeight = sectionHeight + sectionOffsetTop;
      const scrollBottom = scroll + window.innerHeight;

      if (!this.scrolled) {
        if (scrollBottom >= targetHeight) {
          self.page++;
          if (self.hasNextPage) {
            self.hasNextPage = false;
            // For Liquid-rendered content, we need to fetch enough products to get new ones
            if (self.isLiquidRendered) {
              self.isLiquidRendered = false; // Switch to JS mode
              self.cursor = null; // Start from beginning
              // Temporarily increase fetch count to include products beyond what Liquid rendered
              // This ensures we get new products after duplicate filtering
              const baseCount = window.innerWidth > 1024 ? 32 : 16;
              self.numProducts = self.page * baseCount;
              self.fetchProducts(true);
              // Reset numProducts for subsequent fetches (cursor-based pagination)
              self.numProducts = baseCount;
            } else {
              self.fetchProducts(true);
            }
          }
          this.scrolled = true;
        }
      } else {
        if (scrollBottom < targetHeight) {
          this.scrolled = false;
        }
      }
    });
  }

  /**
   * Get IDs of products already rendered in the DOM
   */
  getExistingProductIds() {
    const existingProducts = this.grid?.querySelectorAll('.product-grid-item') || [];
    const ids = new Set();
    existingProducts.forEach(el => {
      // Get ID from class list (products have class like "12345678")
      el.classList.forEach(cls => {
        if (/^\d+$/.test(cls)) {
          ids.add(cls);
        }
      });
    });
    return ids;
  }

  sorting() {
    const self = this;
    const sortBy = document.getElementById('SortBy');
    if (sortBy) {
      sortBy.addEventListener('change', function () {
        self.sortKey = this.value;
        self.cursor = null;
        self.hasNextPage = false;
        if (self.grid) {
          self.grid.innerHTML = '';
        }
        self.fetchProducts();

        const filterSortInput = document.querySelector(`.filter-sort input[value="${self.sortKey}"]`);
        if (filterSortInput) {
          filterSortInput.checked = true;
        }
      });
    }
  }

  filtering() {
    const self = this;
    let filtersArr = [];

    const applyFilters = document.querySelector('.apply-filters');
    if (applyFilters) {
      applyFilters.addEventListener('click', function () {
        filtersArr = [];
        const checkedInputs = document.querySelectorAll('.filters-wrap input[type="checkbox"]:checked');

        checkedInputs.forEach(function (input) {
          let obj;
          if (input.dataset.key == 'productMetafield') {
            obj = { [input.dataset.key]: { key: 'category', namespace: 'custom', value: input.value } };
          } else if (input.dataset.key == 'tag') {
            obj = { tag: input.value };
          } else {
            obj = { [input.dataset.key]: { name: input.dataset.type, value: input.value } };
          }
          filtersArr.push(obj);
        });

        const fromCents = document.getElementById('fromCents');
        const toCents = document.getElementById('toCents');
        if (fromCents && toCents) {
          filtersArr.push({
            price: {
              min: parseInt(fromCents.value, 10) / 100,
              max: parseInt(toCents.value, 10) / 100,
            },
          });
        }

        let unquoted = JSON.stringify(filtersArr).replace(/"([^"]+)":/g, '$1:');

        const checkedSortInput = document.querySelector('.filter-sort input:checked');
        self.sortKey = checkedSortInput ? checkedSortInput.value : null;

        self.filters = unquoted;
        self.cursor = null;
        self.hasNextPage = false;
        if (self.grid) {
          self.grid.innerHTML = '';
        }
        self.fetchProducts();

        const filtersWrapInner = document.querySelector('.filters-wrap-inner');
        if (filtersWrapInner) {
          filtersWrapInner.classList.remove('active');
        }
        setTimeout(() => {
          const filtersWrap = document.querySelector('.filters-wrap');
          if (filtersWrap) {
            filtersWrap.classList.add('hidden');
          }
        }, 500);
      });
    }

    const resetFilters = document.querySelector('.reset-filters');
    if (resetFilters) {
      resetFilters.addEventListener('click', function () {
        const filterInputs = document.querySelectorAll('.filters-wrap input');
        filterInputs.forEach((input) => {
          input.checked = false;
        });
        self.filters = null;
        self.cursor = null;

        if (typeof window.resetPriceSlider === 'function') {
          window.resetPriceSlider();
        } else {
          const fromSlider = document.getElementById('fromSlider');
          const fromInput = document.getElementById('fromInput');
          const toInput = document.getElementById('toInput');
          const toSlider = document.getElementById('toSlider');

          if (fromSlider) fromSlider.value = 0;
          if (fromInput) fromInput.value = '$0.00';
          if (toInput) toInput.value = toInput.dataset.initValue || '';
          if (toSlider) {
            toSlider.value = toSlider.dataset.max || '';
            toSlider.removeAttribute('style');
          }
        }

        self.sortKey = null;
        self.hasNextPage = false;
        if (self.grid) {
          self.grid.innerHTML = '';
        }
        self.fetchProducts();
      });
    }
  }

  mobileGridView() {
    const gridView1 = document.querySelector('.grid-view-1');
    const gridView2 = document.querySelector('.grid-view-2');
    const productsGrid = document.getElementById('products-grid');

    if (gridView1) {
      gridView1.addEventListener('click', function () {
        this.classList.add('hidden');
        if (gridView2) gridView2.classList.remove('hidden');
        if (productsGrid) {
          productsGrid.classList.add('grid-cols-1');
          productsGrid.classList.remove('grid-cols-2');
        }
      });
    }

    if (gridView2) {
      gridView2.addEventListener('click', function () {
        this.classList.add('hidden');
        if (gridView1) gridView1.classList.remove('hidden');
        if (productsGrid) {
          productsGrid.classList.add('grid-cols-2');
          productsGrid.classList.remove('grid-cols-1');
        }
      });
    }
  }
}

customElements.define('collection-grid', CollectionGrid);
